import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryRegistry, baseQueryRouter } from './baseQuery';
import { getCurrentProjectId } from '../../common/context/project-storage';
import { getAuthToken } from '../../common/context/auth/storage';
import { getRouterApiKey } from '../../common/context/router-api-key-storage';
import { routerApiPathPrefix, routerServiceURL } from './defines';
// Note: Import from verna-promptlab-lib when available
// For now, define types locally
export interface PromptSpec {
  promptId: string;
  name: string;
  schemaVersion: string;
  inputs?: Array<{ name: string; type: string; required?: boolean; description?: string }>;
  template: Array<{ role: string; content: string }>;
  outputDefinition: { outputFormat: string };
  constraints?: any;
  [key: string]: any;
}

export interface ExecutionRequest {
  promptId?: string;
  version?: string;
  prompt?: PromptSpec | { promptId: string; version?: string } | string;
  inputs?: Record<string, any>;
  modelPolicy?: {
    primary: { providerType: string; model: string };
  };
  options?: any;
  context?: any;
  trace?: any;
}

export interface ExecutionResult {
  outputText?: string;
  outputJson?: any;
  usage: { promptTokens: number; completionTokens: number; totalTokens?: number };
  cost?: { totalCost: number; currency: string };
  provider: { providerType: string; model: string };
  timing: { startedAt: string; endedAt: string; latencyMs: number };
  eval?: any;
}

// Types
export interface PromptListItem {
  id: string;
  promptId: string;
  name: string;
  description?: string;
  intent?: string;
  tags: string[];
  modelCode?: string;
  fallbackModelCodes?: string[];
  activeVersion?: string;
  outputTemplate?: string;
  modelParameters?: Record<string, any>;
  sourceScenarioId?: string;
  sourceExecutionGroupId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PromptVersion {
  id: string;
  promptId: string;
  version: string;
  isActive?: boolean;
  spec: PromptSpec;
  createdBy?: string;
  changelog?: string;
  sourceScenarioId?: string;
  sourceExecutionGroupId?: string;
  createdAt: string;
}

export interface ScenarioModelRef {
  providerType: string;
  modelId: string;
  accountId?: string;
}

export interface ScenarioAsset {
  fieldName: string;
  label?: string;
  originalName: string;
  mimeType: string;
  size: number;
  storageBucket?: string;
  storageKey?: string;
  storageUri?: string;
  storageUrl?: string;
}

export interface Scenario {
  id: string;
  scenarioId: string;
  name: string;
  description?: string;
  tags?: string[];
  studioState: {
    selectedModels: ScenarioModelRef[];
    prompt: string;
    promptParameters?: Record<string, any>;
    modelParameters?: Record<string, any>;
    outputTemplate?: string;
    inputAssets?: ScenarioAsset[];
    modelSelectionState?: {
      search?: string;
      filters?: {
        text?: boolean;
        image?: boolean;
        video?: boolean;
        audio?: boolean;
        music?: boolean;
        supportsReferenceInput?: boolean;
      };
      sort?: 'most-used' | 'name' | 'provider';
    };
  };
  latestExecutionGroupId?: string;
  sourcePromptId?: string;
  sourcePromptVersion?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExecutionJob {
  jobId: string;
  executionGroupId: string;
  scenarioId?: string;
  interactionId?: string;
  providerType: string;
  modelId: string;
  accountId?: string;
  kind: 'text' | 'media';
  status: string;
  providerJobId?: string;
  providerRequestId?: string;
  outputText?: string;
  outputJson?: any;
  outputs?: MediaOutputAsset[];
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  timing?: { startedAt?: string; endedAt?: string; latencyMs?: number };
  error?: string;
  cancelable?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExecutionBatch {
  executionGroupId: string;
  scenarioId?: string;
  promptId?: string;
  promptVersion?: string;
  status: string;
  summary: {
    total: number;
    queued: number;
    running: number;
    completed: number;
    failed: number;
    canceled: number;
  };
  jobs: ExecutionJob[];
  createdAt: string;
  updatedAt: string;
}

export interface PromptRelease {
  id: string;
  promptId: string;
  channel: string;
  version: string;
  updatedAt: string;
}

export interface InteractionListItem {
  id: string;
  interactionId: string;
  promptId?: string;
  promptName?: string;
  promptVersion?: string;
  status: string;
  AImodel?: string;
  date?: string;
  durationMs?: number;
  prompt?: string;
  response?: string;
  costValue?: number;
  modelId: string;
  providerType: string;
  providerRequestId?: string;
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  tokenUsage?: { promptTokens: number; completionTokens: number; totalTokens: number };
  timing?: { startedAt: string; endedAt: string; latencyMs: number };
  executionContext?: {
    operation?: string;
    operationId?: string;
    runId?: string;
    agentId?: string;
    [key: string]: any;
  };
  isFallback?: boolean;
  fallbackInfo?: {
    attemptedModelCodes?: string[];
    defaultModelCode?: string;
    effectiveModelCode?: string;
    selectedModelCode?: string;
  };
  createdAt: string;
}

export interface InteractionDetail extends InteractionListItem {
  inputs?: Record<string, any>;
  inputFiles?: Array<{
    fieldName?: string;
    originalName?: string;
    mimeType?: string;
    size?: number;
    storageBucket?: string;
    storageKey?: string;
    storageUri?: string;
    storageUrl?: string;
  }>;
  outputText?: string;
  outputJson?: any;
  generatedMedia?: string[];
  queueResponses?: any[];
  rawResponse?: any;
  providerMetadata?: Record<string, any>;
  evalResults?: any;
  error?: string;
}

export interface AlertLogListItem {
  id: string;
  alertId: string;
  createdAt: string;
  severity: string;
  type: string;
  message: string;
  promptId?: string;
  promptName?: string;
  promptVersion?: string;
  interactionId?: string;
  defaultModelCode?: string;
  fallbackModelCode?: string;
  modelCode?: string;
  metadata?: Record<string, any>;
}

export interface CostAnalytics {
  totalCost: number;
  totalCount: number;
  avgCost: number;
  costByPrompt: Array<{ promptId: string; totalCost: number; count: number }>;
  costByModel: Array<{ model: string; totalCost: number; count: number }>;
  costOverTime: Array<{ date: string; totalCost: number; count: number }>;
}

export interface InteractionAnalyticsResponse {
  data: {
    metrics: {
      call_volume: number;
      billable_calls: number;
      total_cost: number;
      avg_latency_ms: number;
      avg_cost_per_call: number;
      rows_used: number;
      data_freshness_utc: string;
    };
    breakdowns: {
      by_provider_model: Array<{
        providerType: string;
        modelId: string;
        call_volume: number;
        billable_calls: number;
        total_cost: number;
        avg_latency_ms: number;
        avg_cost_per_call: number;
      }>;
      by_prompt: Array<{
        promptId: string;
        call_volume: number;
        total_cost: number;
      }>;
      by_time: Array<{
        bucket_start_utc: string;
        call_volume: number;
        billable_calls: number;
        total_cost: number;
        avg_latency_ms: number;
      }>;
    };
  };
  total: number;
  meta: {
    timezone: 'UTC';
    bucket: 'hour' | 'day';
    generated_at_utc: string;
  };
}

export interface Project {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  status?: 'active' | 'archived';
  metadata?: Record<string, any>;
  ownerUserId?: string;
  currentUserRole?: 'owner' | 'admin' | 'user';
  canManageAccess?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  role: 'owner' | 'admin' | 'user';
  status: 'active' | 'revoked';
  acceptedAt?: string;
  revokedAt?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    username: string;
    email: string;
    fullName: string;
    isActive: boolean;
  };
}

export interface ProjectInvitation {
  id: string;
  projectId: string;
  projectName?: string;
  email: string;
  role: 'admin' | 'user';
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired';
  invitedUserId?: string;
  expiresAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  revokedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  requiresLogin?: boolean;
  canSelfRegister?: boolean;
}

export interface ProjectAccessSnapshot {
  members: ProjectMember[];
  invitations: ProjectInvitation[];
}

export interface ProjectApiKey {
  id: string;
  projectId: string;
  keyId: string;
  name: string;
  description?: string;
  tokenPreview: string;
  scopes: string[];
  createdByUserId: string;
  lastUsedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'expired' | 'revoked';
}

export interface ProviderAccount {
  id: string;
  accountId: string;
  providerType: string;
  name: string;
  description?: string;
  active: boolean;
  lastTestResult?: {
    success: boolean;
    error?: string;
    testedAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface RuntimeModel {
  id?: string;
  key: string;
  providerType: string;
  modelId: string;
  providerModelId?: string;
  providerOperation?: string;
  name: string;
  description?: string;
  family?: string;
  modality?: 'text' | 'image' | 'video' | 'audio' | 'music' | 'mixed';
  executionKind?: 'text' | 'media';
  executionMode?: 'sync' | 'async';
  normalizedId?: string;
  contextWindow?: number;
  capabilities?: {
    supportsTextGeneration?: boolean;
    supportsImageGeneration?: boolean;
    supportsVideoGeneration?: boolean;
    supportsAudioGeneration?: boolean;
    supportsMusicGeneration?: boolean;
    supportsFileUpload?: boolean;
    supportsImageInput?: boolean;
    supportsMultipleImages?: boolean;
    supportsStartImage?: boolean;
    supportsEndImage?: boolean;
    supportsMusicReference?: boolean;
    functionCalling?: boolean;
    streaming?: boolean;
    vision?: boolean;
    audio?: boolean;
    contextWindow?: number;
    outputFormats?: string[];
    [key: string]: any;
  };
  acceptedInputs?: {
    file?: boolean;
    maxFileCount?: number;
    image?: boolean;
    multipleImages?: boolean;
    maxImageCount?: number;
    audio?: boolean;
    startImage?: boolean;
    endImage?: boolean;
  };
  inputSchema?: {
    fields?: Array<Record<string, any>>;
    raw?: any;
  };
  pricing?: {
    billingType?: 'tokens' | 'unit' | 'hybrid';
    promptTokenCost?: number;
    completionTokenCost?: number;
    unitPrice?: number;
    unit?: string;
    currency?: string;
    rules?: Array<Record<string, any>>;
    summaryText?: string;
  };
  docs?: {
    providerDocsUrl?: string;
    providerPlaygroundUrl?: string;
    sdkDocsUrl?: string;
  };
  status?: string;
  tags?: string[];
  metadata?: Record<string, any>;
  accountIds?: string[];
}

export interface ModelRegistryRecord extends RuntimeModel {
  id?: string;
  source?: Record<string, any>;
  overrides?: Record<string, any>;
  lastSeenAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ModelFavoriteEntry {
  id: string;
  providerType: string;
  modelId: string;
  modelName?: string;
  scopeLevel: 'project' | 'company';
  scope?: {
    companyId?: string;
    projectId?: string;
    workspaceId?: string;
    environment?: string;
  };
  inheritedFrom?: 'project' | 'company';
  createdAt: string;
  updatedAt: string;
}

export interface ModelFavoritesSnapshot {
  projectFavorites: ModelFavoriteEntry[];
  companyFavorites: ModelFavoriteEntry[];
  effectiveFavorites: ModelFavoriteEntry[];
  favoriteKeys: string[];
}

export type SyncProviderType =
  | 'mureka'
  | 'fal'
  | 'openrouter'
  | 'openai'
  | 'google'
  | 'anthropic'
  | 'replicate'
  | 'perplexity';

export interface ModelSyncStatus {
  providerType: SyncProviderType;
  scope?: {
    projectId?: string;
    workspaceId?: string;
    environment?: string;
  };
  startedAt?: string;
  finishedAt?: string;
  processed?: number;
  created?: number;
  updated?: number;
  deprecated?: number;
  total?: number;
  currentModelId?: string;
  lastMessage?: string;
  errors?: Array<{ modelKey?: string; message: string }>;
  status: 'idle' | 'running' | 'completed' | 'failed';
}

export interface MediaOutputAsset {
  type: 'image' | 'video' | 'audio' | 'file' | 'text' | 'json';
  url?: string;
  mimeType?: string;
  text?: string;
  data?: any;
  storageBucket?: string;
  storageKey?: string;
  storageUri?: string;
}

export interface MediaExecutionRequest {
  providerType: string;
  model: string;
  prompt?: string;
  accountId?: string;
  input?: Record<string, any>;
  options?: Record<string, any>;
  context?: Record<string, any>;
  trace?: Record<string, any>;
  files?: Array<{
    fieldName: string;
    file: File;
  }>;
}

export interface MediaExecutionAccepted {
  jobId: string;
  providerType: string;
  providerJobId: string;
  model: string;
  status: string;
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  streamUrl?: string;
  resultUrl?: string;
  cancelUrl?: string;
  pollAfterMs: number;
  submittedAt: string;
}

export interface MediaExecutionStatus {
  jobId: string;
  providerType: string;
  providerJobId: string;
  model: string;
  status: string;
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  streamUrl?: string;
  resultUrl?: string;
  cancelUrl?: string;
  outputs?: MediaOutputAsset[];
  error?: string;
  updatedAt: string;
}

export interface MediaExecutionResult {
  jobId: string;
  providerRequestId?: string;
  status: string;
  outputs: MediaOutputAsset[];
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  resultUrl?: string;
}

const isRecord = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const asArray = <T>(value: unknown, mapper: (item: any) => T): T[] =>
  Array.isArray(value) ? value.map(mapper) : [];

const projectScopedPath = (path: string, projectId?: string | null): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (projectId) {
    return `/${encodeURIComponent(projectId)}${normalizedPath}`;
  }
  return normalizedPath;
};

const routerProjectScopedPath = (path: string, projectId?: string | null): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (projectId) {
    return `${routerApiPathPrefix}/${encodeURIComponent(projectId)}${normalizedPath}`;
  }
  return `${routerApiPathPrefix}${normalizedPath}`;
};

const buildRouterHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const projectId = getCurrentProjectId();
  const authToken = getAuthToken();
  if (projectId) {
    headers['x-project-id'] = projectId;
    const routerApiKey = getRouterApiKey(projectId);
    if (routerApiKey) {
      headers['x-api-key'] = routerApiKey;
    }
  }
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  return headers;
};

const parseInteractionAnalyticsResponse = (response: any): InteractionAnalyticsResponse => {
  if (!isRecord(response)) {
    return {
      data: {
        metrics: {
          call_volume: 0,
          billable_calls: 0,
          total_cost: 0,
          avg_latency_ms: 0,
          avg_cost_per_call: 0,
          rows_used: 0,
          data_freshness_utc: new Date().toISOString(),
        },
        breakdowns: {
          by_provider_model: [],
          by_prompt: [],
          by_time: [],
        },
      },
      total: 0,
      meta: {
        timezone: 'UTC',
        bucket: 'day',
        generated_at_utc: new Date().toISOString(),
      },
    };
  }

  const data = isRecord(response.data) ? response.data : {};
  const metrics = isRecord(data.metrics) ? data.metrics : {};
  const breakdowns = isRecord(data.breakdowns) ? data.breakdowns : {};
  const rawBucket = asString((response.meta as any)?.bucket, 'day');
  const bucket: 'hour' | 'day' = rawBucket === 'hour' ? 'hour' : 'day';

  return {
    data: {
      metrics: {
        call_volume: asNumber(metrics.call_volume),
        billable_calls: asNumber(metrics.billable_calls),
        total_cost: asNumber(metrics.total_cost),
        avg_latency_ms: asNumber(metrics.avg_latency_ms),
        avg_cost_per_call: asNumber(metrics.avg_cost_per_call),
        rows_used: asNumber(metrics.rows_used),
        data_freshness_utc: asString(metrics.data_freshness_utc, new Date().toISOString()),
      },
      breakdowns: {
        by_provider_model: asArray(breakdowns.by_provider_model, (item) => ({
          providerType: asString(item?.providerType, 'unknown'),
          modelId: asString(item?.modelId, 'unknown'),
          call_volume: asNumber(item?.call_volume),
          billable_calls: asNumber(item?.billable_calls),
          total_cost: asNumber(item?.total_cost),
          avg_latency_ms: asNumber(item?.avg_latency_ms),
          avg_cost_per_call: asNumber(item?.avg_cost_per_call),
        })),
        by_prompt: asArray(breakdowns.by_prompt, (item) => ({
          promptId: asString(item?.promptId, 'unknown'),
          call_volume: asNumber(item?.call_volume),
          total_cost: asNumber(item?.total_cost),
        })),
        by_time: asArray(breakdowns.by_time, (item) => ({
          bucket_start_utc: asString(item?.bucket_start_utc, new Date().toISOString()),
          call_volume: asNumber(item?.call_volume),
          billable_calls: asNumber(item?.billable_calls),
          total_cost: asNumber(item?.total_cost),
          avg_latency_ms: asNumber(item?.avg_latency_ms),
        })),
      },
    },
    total: asNumber(response.total),
    meta: {
      timezone: 'UTC',
      bucket,
      generated_at_utc: asString(
        (response.meta as any)?.generated_at_utc,
        new Date().toISOString(),
      ),
    },
  };
};

const toIsoString = (value: any): string | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const toInteractionText = (value: any): string | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const extractPromptText = (item: any): string | undefined => {
  if (typeof item?.prompt === 'string' && item.prompt.trim()) {
    return item.prompt;
  }
  if (typeof item?.inputs?.prompt === 'string' && item.inputs.prompt.trim()) {
    return item.inputs.prompt;
  }
  const template = item?.promptSpec?.template;
  if (Array.isArray(template)) {
    const content = template
      .map((entry: any) => (typeof entry?.content === 'string' ? entry.content : ''))
      .filter(Boolean)
      .join('\n\n');
    if (content.trim()) return content;
  }
  return undefined;
};

const extractInteractionPayload = (response: any): Record<string, any> => {
  if (!isRecord(response)) return {};
  if (isRecord(response.data)) return extractInteractionPayload(response.data);
  if (isRecord(response._doc)) return extractInteractionPayload(response._doc);
  return response;
};

const normalizeRuntimeModel = (item: any): RuntimeModel => {
  const providerType = asString(item?.providerType, 'unknown');
  const modelId = asString(item?.modelId || item?.providerModelId || item?.id, 'unknown');
  const capabilities = isRecord(item?.capabilities) ? item.capabilities : undefined;
  const acceptedInputs = isRecord(item?.acceptedInputs) ? item.acceptedInputs : undefined;
  const pricing = isRecord(item?.pricing) ? item.pricing : undefined;
  const docs = isRecord(item?.docs) ? item.docs : undefined;
  const inputSchema = isRecord(item?.inputSchema) ? item.inputSchema : undefined;
  const metadata = isRecord(item?.metadata) ? item.metadata : undefined;

  return {
    id: item?.id ? String(item.id) : undefined,
    key: asString(item?.key, `${providerType}::${modelId}`),
    providerType,
    modelId,
    providerModelId: item?.providerModelId ? String(item.providerModelId) : undefined,
    providerOperation: item?.providerOperation ? String(item.providerOperation) : undefined,
    name: asString(item?.name, modelId),
    description: item?.description ? String(item.description) : undefined,
    family: item?.family ? String(item.family) : undefined,
    modality: item?.modality,
    executionKind: item?.executionKind,
    executionMode: item?.executionMode,
    normalizedId: item?.normalizedId ? String(item.normalizedId) : undefined,
    contextWindow: typeof item?.contextWindow === 'number' ? item.contextWindow : undefined,
    capabilities,
    acceptedInputs,
    inputSchema: inputSchema
      ? {
          fields: Array.isArray(inputSchema.fields) ? inputSchema.fields : undefined,
          raw: inputSchema.raw,
        }
      : undefined,
    pricing: pricing
      ? {
          billingType: pricing.billingType,
          promptTokenCost: typeof pricing.promptTokenCost === 'number' ? pricing.promptTokenCost : undefined,
          completionTokenCost: typeof pricing.completionTokenCost === 'number' ? pricing.completionTokenCost : undefined,
          unitPrice: typeof pricing.unitPrice === 'number' ? pricing.unitPrice : undefined,
          unit: pricing.unit ? String(pricing.unit) : undefined,
          currency: pricing.currency ? String(pricing.currency) : undefined,
          rules: Array.isArray(pricing.rules) ? pricing.rules : undefined,
          summaryText: pricing.summaryText ? String(pricing.summaryText) : undefined,
        }
      : undefined,
    docs: docs
      ? {
          providerDocsUrl: docs.providerDocsUrl ? String(docs.providerDocsUrl) : undefined,
          providerPlaygroundUrl: docs.providerPlaygroundUrl ? String(docs.providerPlaygroundUrl) : undefined,
          sdkDocsUrl: docs.sdkDocsUrl ? String(docs.sdkDocsUrl) : undefined,
        }
      : undefined,
    status: item?.status ? String(item.status) : undefined,
    tags: Array.isArray(item?.tags) ? item.tags.map((tag: any) => String(tag)) : undefined,
    metadata,
    accountIds: Array.isArray(item?.accountIds)
      ? item.accountIds.map((accountId: any) => String(accountId))
      : undefined,
  };
};

const normalizeRuntimeModelsResponse = (
  response: any,
): { generatedAt: string; total: number; models: RuntimeModel[] } => {
  const payload = isRecord(response?.data) ? response.data : response;
  const rows = Array.isArray(payload?.models)
    ? payload.models
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];
  const models = rows
    .map((item) => normalizeRuntimeModel(item))
    .filter((item) => item.providerType && item.providerType !== 'unknown' && item.modelId && item.modelId !== 'unknown');

  return {
    generatedAt: asString(payload?.generatedAt || payload?.generated_at_utc, new Date().toISOString()),
    total: typeof payload?.total === 'number' ? payload.total : models.length,
    models,
  };
};

const normalizeInteraction = (item: any): InteractionListItem => {
  const providerType = item?.providerType || 'unknown';
  const modelId = item?.modelId || item?.providerModelId || item?.AImodel || 'unknown';
  const interactionId = item?.interactionId || item?.id || item?._id || '';
  const date = toIsoString(item?.date || item?.timing?.startedAt || item?.createdAt);
  const createdAt = date || new Date().toISOString();
  const durationMs =
    typeof item?.durationMs === 'number'
      ? item.durationMs
      : typeof item?.duration === 'number'
        ? item.duration
        : typeof item?.timing?.latencyMs === 'number'
          ? item.timing.latencyMs
          : undefined;
  const totalCost =
    typeof item?.cost === 'number'
      ? item.cost
      : typeof item?.cost?.totalCost === 'number'
        ? item.cost.totalCost
        : undefined;
  const prompt = extractPromptText(item);
  const response =
    toInteractionText(item?.response) ??
    toInteractionText(item?.outputText) ??
    toInteractionText(item?.outputJson?.assets) ??
    toInteractionText(item?.outputJson) ??
    toInteractionText(item?.rawResponse);
  const fallbackInfo = isRecord(item?.providerMetadata?.fallbackInfo)
    ? item.providerMetadata.fallbackInfo
    : undefined;
  const isFallback = Boolean(item?.isFallback || item?.providerMetadata?.isFallback || fallbackInfo);

  return {
    id: String(item?.id || item?._id || interactionId),
    interactionId: String(interactionId),
    promptId: item?.promptId,
    promptName: item?.promptName,
    promptVersion: item?.promptVersion,
    status: item?.status || 'unknown',
    AImodel: item?.AImodel || `${providerType}/${modelId}`,
    date: createdAt,
    durationMs,
    prompt,
    response,
    costValue: totalCost,
    modelId,
    providerType,
    providerRequestId:
      item?.providerRequestId ||
      item?.openRouterID ||
      item?.providerMetadata?.providerRequestId ||
      item?.providerMetadata?.requestId,
    cost: item?.cost
      ? {
          totalCost:
            typeof item.cost.totalCost === 'number'
              ? item.cost.totalCost
              : typeof totalCost === 'number'
                ? totalCost
                : 0,
          currency: item.cost.currency || 'USD',
          promptCost: item.cost.promptCost,
          completionCost: item.cost.completionCost,
          details: item.cost.details,
        }
      : undefined,
    tokenUsage: item?.tokenUsage || item?.usage,
    timing: item?.timing
      ? item.timing
      : date
        ? {
            startedAt: date,
            endedAt: toIsoString(item?.timing?.endedAt || item?.updatedAt || item?.createdAt) || date,
            latencyMs: typeof durationMs === 'number' ? durationMs : 0,
          }
        : undefined,
    executionContext: item?.executionContext || {
      operation: item?.operation,
      operationId: item?.operationID,
      runId: item?.runID,
      agentId: item?.agentID,
    },
    isFallback,
    fallbackInfo: fallbackInfo
      ? {
          attemptedModelCodes: Array.isArray(fallbackInfo.attemptedModelCodes)
            ? fallbackInfo.attemptedModelCodes.map((entry: any) => String(entry))
            : undefined,
          defaultModelCode:
            typeof fallbackInfo.defaultModelCode === 'string'
              ? fallbackInfo.defaultModelCode
              : undefined,
          effectiveModelCode:
            typeof fallbackInfo.effectiveModelCode === 'string'
              ? fallbackInfo.effectiveModelCode
              : undefined,
          selectedModelCode:
            typeof fallbackInfo.selectedModelCode === 'string'
              ? fallbackInfo.selectedModelCode
              : undefined,
        }
      : undefined,
    createdAt,
  };
};

const normalizeAlertLog = (item: any): AlertLogListItem => ({
  id: String(item?.id || item?._id || item?.alertId || ''),
  alertId: String(item?.alertId || item?.id || item?._id || ''),
  createdAt: toIsoString(item?.createdAt) || new Date().toISOString(),
  severity: asString(item?.severity, 'warning'),
  type: asString(item?.type, 'unknown'),
  message: asString(item?.message, ''),
  promptId: item?.promptId ? String(item.promptId) : undefined,
  promptName: item?.promptName ? String(item.promptName) : undefined,
  promptVersion: item?.promptVersion ? String(item.promptVersion) : undefined,
  interactionId: item?.interactionId ? String(item.interactionId) : undefined,
  defaultModelCode: item?.defaultModelCode ? String(item.defaultModelCode) : undefined,
  fallbackModelCode: item?.fallbackModelCode ? String(item.fallbackModelCode) : undefined,
  modelCode: item?.modelCode ? String(item.modelCode) : undefined,
  metadata: isRecord(item?.metadata) ? item.metadata : undefined,
});

// Registry API
export const registryApi = createApi({
  reducerPath: 'registryApi',
  baseQuery: baseQueryRegistry,
  tagTypes: [
    'Projects',
    'ProjectApiKeys',
    'Prompts',
    'Versions',
    'Releases',
    'Interactions',
    'AlertLogs',
    'ProviderAccounts',
    'RuntimeModels',
    'ModelFavorites',
  ],
  endpoints: (builder) => ({
    // Projects
    listProjects: builder.query<Project[], { q?: string; status?: string } | void>({
      query: (params) => ({
        url: '/projects',
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        return rows.filter(isRecord) as Project[];
      },
      providesTags: ['Projects'],
    }),

    createProject: builder.mutation<
      Project,
      { projectId: string; name: string; description?: string; status?: 'active' | 'archived' }
    >({
      query: (body) => ({
        url: '/projects',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),

    updateProject: builder.mutation<
      Project,
      { projectId: string; name?: string; description?: string; status?: 'active' | 'archived' }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),

    deleteProject: builder.mutation<{ success: boolean }, { projectId: string; cascade?: boolean }>({
      query: ({ projectId, cascade = true }) => ({
        url: `/projects/${projectId}`,
        method: 'DELETE',
        params: { cascade },
      }),
      invalidatesTags: ['Projects', 'Prompts', 'Versions', 'Releases', 'Interactions'],
    }),

    getProjectAccess: builder.query<ProjectAccessSnapshot, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/access`,
        method: 'GET',
      }),
      providesTags: ['Projects'],
    }),

    listProjectApiKeys: builder.query<ProjectApiKey[], { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/api-keys`,
        method: 'GET',
      }),
      transformResponse: (response: any) =>
        (Array.isArray(response) ? response : []).filter(isRecord) as ProjectApiKey[],
      providesTags: ['ProjectApiKeys'],
    }),

    createProjectApiKey: builder.mutation<
      { apiKey: ProjectApiKey; token: string },
      {
        projectId: string;
        name: string;
        description?: string;
        expiresAt?: string;
        scopes?: string[];
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/api-keys`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ProjectApiKeys'],
    }),

    revokeProjectApiKey: builder.mutation<{ success: boolean }, { projectId: string; keyId: string }>({
      query: ({ projectId, keyId }) => ({
        url: `/projects/${projectId}/api-keys/${encodeURIComponent(keyId)}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: ['ProjectApiKeys'],
    }),

    inviteProjectUser: builder.mutation<
      { invitation: ProjectInvitation; inviteUrl?: string; token?: string },
      { projectId: string; email: string; fullName?: string; role: 'admin' | 'user' }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/invitations`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),

    updateProjectMemberRole: builder.mutation<
      ProjectMember,
      { projectId: string; userId: string; role: 'admin' | 'user' }
    >({
      query: ({ projectId, userId, ...body }) => ({
        url: `/projects/${projectId}/members/${userId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),

    revokeProjectMember: builder.mutation<{ success: boolean }, { projectId: string; userId: string }>({
      query: ({ projectId, userId }) => ({
        url: `/projects/${projectId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Projects'],
    }),

    revokeProjectInvitation: builder.mutation<{ success: boolean }, { projectId: string; invitationId: string }>({
      query: ({ projectId, invitationId }) => ({
        url: `/projects/${projectId}/invitations/${invitationId}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: ['Projects'],
    }),

    listMyProjectInvitations: builder.query<ProjectInvitation[], void>({
      query: () => ({
        url: '/auth/invitations',
        method: 'GET',
      }),
      transformResponse: (response: any) =>
        (Array.isArray(response) ? response : []).filter(isRecord) as ProjectInvitation[],
      providesTags: ['Projects'],
    }),

    acceptMyProjectInvitation: builder.mutation<{ success: boolean }, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: `/auth/invitations/${invitationId}/accept`,
        method: 'POST',
      }),
      invalidatesTags: ['Projects'],
    }),

    declineMyProjectInvitation: builder.mutation<{ success: boolean }, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: `/auth/invitations/${invitationId}/decline`,
        method: 'POST',
      }),
      invalidatesTags: ['Projects'],
    }),

    listProviderAccounts: builder.query<
      ProviderAccount[],
      { providerType?: string; active?: boolean; q?: string } | void
    >({
      query: (params) => ({
        url: projectScopedPath('/provider-accounts', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) =>
        (Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []
      ).filter(isRecord) as ProviderAccount[],
      providesTags: ['ProviderAccounts'],
    }),

    getModelFavorites: builder.query<ModelFavoritesSnapshot, void>({
      query: () => ({
        url: projectScopedPath('/model-favorites', getCurrentProjectId()),
        method: 'GET',
      }),
      transformResponse: (response: any) => ({
        projectFavorites: asArray(response?.projectFavorites, (item) => item as ModelFavoriteEntry),
        companyFavorites: asArray(response?.companyFavorites, (item) => item as ModelFavoriteEntry),
        effectiveFavorites: asArray(response?.effectiveFavorites, (item) => item as ModelFavoriteEntry),
        favoriteKeys: asArray(response?.favoriteKeys, (item) => asString(item)).filter(Boolean),
      }),
      providesTags: ['ModelFavorites'],
    }),

    listModelRegistryRecords: builder.query<
      { data: ModelRegistryRecord[]; total: number },
      { providerType?: string; status?: string; q?: string } | void
    >({
      query: (params) => ({
        url: '/models',
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        return {
          data: rows.filter(isRecord) as ModelRegistryRecord[],
          total: typeof response?.total === 'number' ? response.total : rows.length,
        };
      },
      providesTags: ['RuntimeModels'],
    }),

    createModelRegistryRecord: builder.mutation<ModelRegistryRecord, Record<string, any>>({
      query: (body) => ({
        url: '/models',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RuntimeModels'],
    }),

    updateModelRegistryRecord: builder.mutation<
      ModelRegistryRecord,
      { id: string; updates: Record<string, any> }
    >({
      query: ({ id, updates }) => ({
        url: `/models/by-id/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['RuntimeModels'],
    }),

    upsertModelFavorite: builder.mutation<
      ModelFavoriteEntry,
      { providerType: string; modelId: string; modelName?: string; scopeLevel: 'project' | 'company' }
    >({
      query: (body) => ({
        url: projectScopedPath('/model-favorites', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ModelFavorites'],
    }),

    deleteModelFavorite: builder.mutation<
      { success: boolean },
      { providerType: string; modelId: string; scopeLevel: 'project' | 'company' }
    >({
      query: ({ providerType, modelId, scopeLevel }) => ({
        url: projectScopedPath(
          `/model-favorites/${encodeURIComponent(scopeLevel)}/${encodeURIComponent(providerType)}/${encodeURIComponent(modelId)}`,
          getCurrentProjectId(),
        ),
        method: 'DELETE',
      }),
      invalidatesTags: ['ModelFavorites'],
    }),

    getModelSyncStatus: builder.query<ModelSyncStatus, { providerType: SyncProviderType }>({
      query: ({ providerType }) => ({
        url: `/model-sync/${providerType}/status`,
        method: 'GET',
      }),
    }),

    runModelSync: builder.mutation<ModelSyncStatus, { providerType: SyncProviderType }>({
      query: ({ providerType }) => ({
        url: `/model-sync/${providerType}/run`,
        method: 'POST',
      }),
      invalidatesTags: ['RuntimeModels'],
    }),

    runAllModelSync: builder.mutation<ModelSyncStatus[], void>({
      query: () => ({
        url: '/model-sync/run-all',
        method: 'POST',
      }),
      invalidatesTags: ['RuntimeModels'],
    }),

    createProviderAccount: builder.mutation<
      ProviderAccount,
      {
        accountId: string;
        providerType: string;
        name: string;
        description?: string;
        credentials: Record<string, any>;
        active?: boolean;
      }
    >({
      query: (body) => ({
        url: projectScopedPath('/provider-accounts', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ProviderAccounts', 'RuntimeModels'],
    }),

    updateProviderAccount: builder.mutation<
      ProviderAccount,
      {
        accountId: string;
        name?: string;
        description?: string;
        credentials?: Record<string, any>;
        active?: boolean;
      }
    >({
      query: ({ accountId, ...body }) => ({
        url: projectScopedPath(`/provider-accounts/${accountId}`, getCurrentProjectId()),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ProviderAccounts', 'RuntimeModels'],
    }),

    deleteProviderAccount: builder.mutation<{ success: boolean }, { accountId: string }>({
      query: ({ accountId }) => ({
        url: projectScopedPath(`/provider-accounts/${accountId}`, getCurrentProjectId()),
        method: 'DELETE',
      }),
      invalidatesTags: ['ProviderAccounts', 'RuntimeModels'],
    }),

    listScenarios: builder.query<
      { data: Scenario[]; total: number },
      {
        q?: string;
        providerType?: string;
        modelId?: string;
        tag?: string;
        sourcePromptId?: string;
        createdBy?: string;
        limit?: number;
        offset?: number;
        orderBy?: string;
        orderDir?: 'asc' | 'desc';
      } | void
    >({
      query: (params) => ({
        url: projectScopedPath('/scenarios', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        return {
          data: rows.filter(isRecord) as Scenario[],
          total: typeof response?.total === 'number' ? response.total : rows.length,
        };
      },
      providesTags: ['Prompts'],
    }),

    getScenario: builder.query<Scenario, string>({
      query: (scenarioId) => ({
        url: projectScopedPath(`/scenarios/${scenarioId}`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: ['Prompts'],
    }),

    createScenario: builder.mutation<
      Scenario,
      {
        name: string;
        description?: string;
        tags?: string[];
        studioState: Scenario['studioState'];
        sourcePromptId?: string;
        sourcePromptVersion?: string;
        createdBy?: string;
      }
    >({
      query: (body) => ({
        url: projectScopedPath('/scenarios', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),

    updateScenario: builder.mutation<
      Scenario,
      {
        scenarioId: string;
        name?: string;
        description?: string;
        tags?: string[];
        studioState?: Scenario['studioState'];
        latestExecutionGroupId?: string;
        sourcePromptId?: string;
        sourcePromptVersion?: string;
        updatedBy?: string;
      }
    >({
      query: ({ scenarioId, ...body }) => ({
        url: projectScopedPath(`/scenarios/${scenarioId}`, getCurrentProjectId()),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),

    // Prompts
    listPrompts: builder.query<
      { data: PromptListItem[]; total: number },
      { search?: string; intent?: string; tag?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: projectScopedPath('/prompts', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response?.data)) {
          const rows = response.data.filter(isRecord);
          return {
            data: rows,
            total: typeof response.total === 'number' ? response.total : rows.length,
          };
        }
        if (Array.isArray(response)) {
          const rows = response.filter(isRecord);
          return { data: rows, total: rows.length };
        }
        return { data: [], total: 0 };
      },
      providesTags: ['Prompts'],
    }),

    getPrompt: builder.query<PromptListItem, string>({
      query: (promptId) => projectScopedPath(`/prompts/${promptId}`, getCurrentProjectId()),
      providesTags: (result, error, promptId) => [{ type: 'Prompts', id: promptId }],
    }),

    createPrompt: builder.mutation<
      PromptListItem,
      { promptId: string; name: string; description?: string; intent?: string; tags?: string[]; modelCode?: string; fallbackModelCodes?: string[]; outputTemplate?: string; inputParameters?: any[]; modelParameters?: Record<string, any>; sourceScenarioId?: string; sourceExecutionGroupId?: string }
    >({
      query: (body) => ({
        url: projectScopedPath('/prompts', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),

    updatePrompt: builder.mutation<
      PromptListItem,
      { promptId: string; name?: string; description?: string; intent?: string; tags?: string[]; modelCode?: string; fallbackModelCodes?: string[]; outputTemplate?: string; inputParameters?: any[]; modelParameters?: Record<string, any>; sourceScenarioId?: string; sourceExecutionGroupId?: string }
    >({
      query: ({ promptId, ...body }) => ({
        url: projectScopedPath(`/prompts/${promptId}`, getCurrentProjectId()),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Prompts', id: promptId },
        'Prompts',
      ],
    }),

    deletePrompt: builder.mutation<{ success: boolean }, string>({
      query: (promptId) => ({
        url: projectScopedPath(`/prompts/${promptId}`, getCurrentProjectId()),
        method: 'DELETE',
      }),
      invalidatesTags: ['Prompts', 'Versions', 'Releases'],
    }),

    // Versions
    listVersions: builder.query<PromptVersion[], string>({
      query: (promptId) =>
        projectScopedPath(`/prompts/${promptId}/versions`, getCurrentProjectId()),
      transformResponse: (response: any) =>
        (Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []
        ).filter(isRecord) as PromptVersion[],
      providesTags: (result, error, promptId) => [
        { type: 'Versions', id: promptId },
      ],
    }),

    getVersion: builder.query<PromptVersion, { promptId: string; version: string }>({
      query: ({ promptId, version }) =>
        projectScopedPath(`/prompts/${promptId}/versions/${version}`, getCurrentProjectId()),
      providesTags: (result, error, { promptId, version }) => [
        { type: 'Versions', id: `${promptId}-${version}` },
      ],
    }),

    createVersion: builder.mutation<
      PromptVersion,
      { promptId: string; spec: PromptSpec; version?: string; createdBy?: string; changelog?: string; activate?: boolean; sourceScenarioId?: string; sourceExecutionGroupId?: string }
    >({
      query: ({ promptId, ...body }) => ({
        url: projectScopedPath(`/prompts/${promptId}/versions`, getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Versions', id: promptId },
        'Prompts',
      ],
    }),

    deleteVersion: builder.mutation<
      { success: boolean },
      { promptId: string; version: string }
    >({
      query: ({ promptId, version }) => ({
        url: projectScopedPath(`/prompts/${promptId}/versions/${version}`, getCurrentProjectId()),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Versions', id: promptId },
        'Prompts',
      ],
    }),

    setActiveVersion: builder.mutation<
      PromptVersion,
      { promptId: string; version: string }
    >({
      query: ({ promptId, version }) => ({
        url: projectScopedPath(
          `/prompts/${promptId}/versions/${version}/active`,
          getCurrentProjectId(),
        ),
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Versions', id: promptId },
        'Prompts',
      ],
    }),

    // Releases
    listReleases: builder.query<PromptRelease[], string>({
      query: (promptId) =>
        projectScopedPath(`/prompts/${promptId}/releases`, getCurrentProjectId()),
      transformResponse: (response: any) =>
        (Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []
        ).filter(isRecord) as PromptRelease[],
      providesTags: (result, error, promptId) => [
        { type: 'Releases', id: promptId },
      ],
    }),

    setRelease: builder.mutation<
      PromptRelease,
      { promptId: string; channel: string; version: string }
    >({
      query: ({ promptId, channel, ...body }) => ({
        url: projectScopedPath(`/prompts/${promptId}/releases/${channel}`, getCurrentProjectId()),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Releases', id: promptId },
      ],
    }),

    // Bundles
    exportBundle: builder.query<
      any,
      { promptIds?: string[]; includeTestcases?: boolean }
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.promptIds) {
          searchParams.append('promptIds', params.promptIds.join(','));
        }
        if (params.includeTestcases) {
          searchParams.append('includeTestcases', 'true');
        }
        return {
          url: `${projectScopedPath('/bundles/export', getCurrentProjectId())}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
          method: 'GET',
        };
      },
    }),

    importBundle: builder.mutation<
      { imported: number; skipped: number; errors: any[] },
      { bundle: any; options?: { mode?: 'merge' | 'overwrite'; allowNewPromptIds?: boolean } }
    >({
      query: ({ bundle, options }) => ({
        url: projectScopedPath('/bundles/import', getCurrentProjectId()),
        method: 'POST',
        body: { bundle, options },
      }),
      invalidatesTags: ['Prompts', 'Versions', 'Releases'],
    }),

    // Interactions
    listInteractions: builder.query<
      { data: InteractionListItem[]; total: number },
      {
        promptId?: string;
        providerType?: string;
        modelId?: string;
        status?: string;
        startDate?: string;
        endDate?: string;
        limit?: number;
        offset?: number;
        skip?: number; // backward compatibility
        orderBy?: string;
        orderDir?: 'asc' | 'desc';
      }
    >({
      query: (params) => ({
        url: projectScopedPath('/interactions', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const envelopeData = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        const normalized = envelopeData.filter(isRecord).map(normalizeInteraction);
        return {
          data: normalized,
          total: typeof response?.total === 'number' ? response.total : normalized.length,
        };
      },
      providesTags: ['Interactions'],
    }),

    listAlertLogs: builder.query<
      { data: AlertLogListItem[]; total: number },
      {
        q?: string;
        promptId?: string;
        severity?: string;
        type?: string;
        limit?: number;
        offset?: number;
        skip?: number;
        orderBy?: string;
        orderDir?: 'asc' | 'desc';
      }
    >({
      query: (params) => ({
        url: projectScopedPath('/alert-logs', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const envelopeData = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const normalized = envelopeData.filter(isRecord).map(normalizeAlertLog);
        return {
          data: normalized,
          total: typeof response?.total === 'number' ? response.total : normalized.length,
        };
      },
      providesTags: ['AlertLogs'],
    }),

    getInteractionAnalytics: builder.query<
      InteractionAnalyticsResponse,
      {
        startDate?: string;
        endDate?: string;
        status?: string;
        providerType?: string;
        modelId?: string;
        bucket?: 'hour' | 'day';
      }
    >({
      query: (params) => ({
        url: projectScopedPath('/interactions/analytics/summary', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => parseInteractionAnalyticsResponse(response),
    }),

    getInteraction: builder.query<InteractionDetail, string>({
      query: (interactionId) =>
        projectScopedPath(`/interactions/${interactionId}`, getCurrentProjectId()),
      transformResponse: (response: any) => {
        const source = extractInteractionPayload(response);
        const normalized = normalizeInteraction(source);
        return {
          ...source,
          ...normalized,
          inputs: source.inputs,
          inputFiles: source.inputFiles ?? source.input_files,
          outputText: source.outputText ?? source.output_text,
          outputJson: source.outputJson ?? source.output_json,
          generatedMedia: source.generatedMedia ?? source.generated_media,
          queueResponses: source.queueResponses ?? source.queue_responses,
          rawResponse: source.rawResponse ?? source.raw_response,
          providerMetadata: source.providerMetadata,
          evalResults: source.evalResults,
          error: source.error,
        };
      },
    }),
  }),
});

// Router API (for execution)
export const routerApi = createApi({
  reducerPath: 'routerApi',
  baseQuery: baseQueryRouter,
  tagTypes: ['Interactions', 'RuntimeModels', 'MediaJobs', 'ExecutionBatches'],
  endpoints: (builder) => ({
    executePrompt: builder.mutation<ExecutionResult, ExecutionRequest>({
      query: (body) => {
        const projectId = getCurrentProjectId();
        const requestBody: ExecutionRequest = projectId
          ? {
              ...body,
              context: {
                ...(body.context || {}),
                projectId,
              },
            }
          : body;
        return {
          url: routerProjectScopedPath('/execute', projectId || body.context?.projectId),
          method: 'POST',
          body: requestBody,
        };
      },
      invalidatesTags: ['Interactions'],
    }),

    listProviders: builder.query<any[], void>({
      query: () => ({
        url: routerProjectScopedPath('/providers', getCurrentProjectId()),
        method: 'GET',
      }),
    }),

    listRuntimeModels: builder.query<
      { generatedAt: string; total: number; models: RuntimeModel[] },
      void
    >({
      query: () => ({
        url: `${routerApiPathPrefix}/providers/models`,
        method: 'GET',
      }),
      transformResponse: (response: any) => normalizeRuntimeModelsResponse(response),
      providesTags: ['RuntimeModels'],
    }),

    testProvider: builder.mutation<
      { success: boolean; error?: string },
      { type: string; credentials?: any }
    >({
      query: ({ type, credentials }) => ({
        url: routerProjectScopedPath(`/providers/${type}/test`, getCurrentProjectId()),
        method: 'POST',
        body: { credentials },
      }),
    }),

    submitMediaExecution: builder.mutation<MediaExecutionAccepted, MediaExecutionRequest>({
      async queryFn(request) {
        try {
          const projectId = getCurrentProjectId() || request.context?.projectId?.toString();
          const formData = new FormData();
          formData.set(
            'payload',
            JSON.stringify({
              providerType: request.providerType,
              model: request.model,
              prompt: request.prompt,
              accountId: request.accountId,
              input: request.input,
              options: request.options,
              context: request.context,
              trace: request.trace,
              fileDescriptors: (request.files || []).map((entry) => ({
                fieldName: entry.fieldName,
              })),
            }),
          );

          for (const entry of request.files || []) {
            formData.append('files', entry.file, entry.file.name);
          }

          const response = await fetch(
            `${routerServiceURL}${routerProjectScopedPath('/media/execute', projectId)}`,
            {
              method: 'POST',
              headers: buildRouterHeaders(),
              body: formData,
            },
          );

          const data = await response.json().catch(() => null);
          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data,
              } as any,
            };
          }

          return { data: data as MediaExecutionAccepted };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error?.message || 'Failed to submit media execution',
            } as any,
          };
        }
      },
      invalidatesTags: ['MediaJobs', 'Interactions'],
    }),

    getMediaExecutionStatus: builder.query<MediaExecutionStatus, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/media/jobs/${jobId}`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: (result, error, { jobId }) => [{ type: 'MediaJobs', id: jobId }],
    }),

    getMediaExecutionResult: builder.query<MediaExecutionResult, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/media/jobs/${jobId}/result`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: (result, error, { jobId }) => [{ type: 'MediaJobs', id: `${jobId}-result` }],
    }),

    cancelMediaExecution: builder.mutation<MediaExecutionStatus, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/media/jobs/${jobId}/cancel`, getCurrentProjectId()),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'MediaJobs', id: jobId },
        { type: 'Interactions' },
      ],
    }),

    createExecutionBatch: builder.mutation<
      ExecutionBatch,
      {
        scenarioId?: string;
        promptId?: string;
        version?: string;
        shared: {
          prompt: string;
          promptParameters?: Record<string, any>;
          modelParameters?: Record<string, any>;
          outputTemplate?: string;
          inputAssets?: ScenarioAsset[];
        };
        models: ScenarioModelRef[];
        context?: Record<string, any>;
        trace?: Record<string, any>;
        files?: Array<{
          fieldName: string;
          file: File;
        }>;
      }
    >({
      async queryFn(request) {
        try {
          const projectId = getCurrentProjectId() || request.context?.projectId?.toString();
          const requestBody = {
            ...request,
            context: projectId
              ? {
                  ...(request.context || {}),
                  projectId,
                }
              : request.context,
          };

          if (request.files?.length) {
            const formData = new FormData();
            formData.set(
              'payload',
              JSON.stringify({
                scenarioId: requestBody.scenarioId,
                promptId: requestBody.promptId,
                version: requestBody.version,
                shared: requestBody.shared,
                models: requestBody.models,
                context: requestBody.context,
                trace: requestBody.trace,
                fileDescriptors: request.files.map((entry) => ({
                  fieldName: entry.fieldName,
                })),
              }),
            );
            for (const entry of request.files) {
              formData.append('files', entry.file, entry.file.name);
            }

            const response = await fetch(
              `${routerServiceURL}${routerProjectScopedPath('/execute/batches', projectId)}`,
              {
                method: 'POST',
                headers: buildRouterHeaders(),
                body: formData,
              },
            );

            const data = await response.json().catch(() => null);
            if (!response.ok) {
              return {
                error: {
                  status: response.status,
                  data,
                } as any,
              };
            }

            return { data: data as ExecutionBatch };
          }

          const response = await fetch(
            `${routerServiceURL}${routerProjectScopedPath('/execute/batches', projectId)}`,
            {
              method: 'POST',
              headers: {
                ...buildRouterHeaders(),
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
            },
          );

          const data = await response.json().catch(() => null);
          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data,
              } as any,
            };
          }

          return { data: data as ExecutionBatch };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error?.message || 'Failed to create execution batch',
            } as any,
          };
        }
      },
      invalidatesTags: ['ExecutionBatches', 'Interactions'],
    }),

    getExecutionBatch: builder.query<ExecutionBatch, { executionGroupId: string }>({
      query: ({ executionGroupId }) => ({
        url: routerProjectScopedPath(`/execute/batches/${executionGroupId}`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: (result, error, { executionGroupId }) => [
        { type: 'ExecutionBatches', id: executionGroupId },
      ],
    }),

    getExecutionJob: builder.query<ExecutionJob, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/execute/jobs/${jobId}`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: (result, error, { jobId }) => [{ type: 'ExecutionBatches', id: jobId }],
    }),

    cancelExecutionBatch: builder.mutation<ExecutionBatch, { executionGroupId: string }>({
      query: ({ executionGroupId }) => ({
        url: routerProjectScopedPath(
          `/execute/batches/${executionGroupId}/cancel`,
          getCurrentProjectId(),
        ),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { executionGroupId }) => [
        { type: 'ExecutionBatches', id: executionGroupId },
        'Interactions',
      ],
    }),

    cancelExecutionJob: builder.mutation<ExecutionJob, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/execute/jobs/${jobId}/cancel`, getCurrentProjectId()),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'ExecutionBatches', id: jobId },
        'Interactions',
      ],
    }),
  }),
});

export const {
  useListProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useListProjectApiKeysQuery,
  useCreateProjectApiKeyMutation,
  useRevokeProjectApiKeyMutation,
  useListModelRegistryRecordsQuery,
  useCreateModelRegistryRecordMutation,
  useUpdateModelRegistryRecordMutation,
  useGetProjectAccessQuery,
  useInviteProjectUserMutation,
  useUpdateProjectMemberRoleMutation,
  useRevokeProjectMemberMutation,
  useRevokeProjectInvitationMutation,
  useListMyProjectInvitationsQuery,
  useAcceptMyProjectInvitationMutation,
  useDeclineMyProjectInvitationMutation,
  useListPromptsQuery,
  useGetPromptQuery,
  useCreatePromptMutation,
  useUpdatePromptMutation,
  useDeletePromptMutation,
  useListVersionsQuery,
  useGetVersionQuery,
  useCreateVersionMutation,
  useDeleteVersionMutation,
  useSetActiveVersionMutation,
  useListReleasesQuery,
  useSetReleaseMutation,
  useExportBundleQuery,
  useImportBundleMutation,
  useListInteractionsQuery,
  useLazyListInteractionsQuery,
  useListAlertLogsQuery,
  useLazyListAlertLogsQuery,
  useGetInteractionAnalyticsQuery,
  useGetInteractionQuery,
  useListProviderAccountsQuery,
  useGetModelFavoritesQuery,
  useGetModelSyncStatusQuery,
  useRunModelSyncMutation,
  useRunAllModelSyncMutation,
  useUpsertModelFavoriteMutation,
  useDeleteModelFavoriteMutation,
  useCreateProviderAccountMutation,
  useUpdateProviderAccountMutation,
  useDeleteProviderAccountMutation,
  useListScenariosQuery,
  useGetScenarioQuery,
  useCreateScenarioMutation,
  useUpdateScenarioMutation,
} = registryApi;

export const {
  useExecutePromptMutation,
  useSubmitMediaExecutionMutation,
  useLazyGetMediaExecutionStatusQuery,
  useLazyGetMediaExecutionResultQuery,
  useCancelMediaExecutionMutation,
  useListProvidersQuery,
  useListRuntimeModelsQuery,
  useTestProviderMutation,
  useCreateExecutionBatchMutation,
  useLazyGetExecutionBatchQuery,
  useLazyGetExecutionJobQuery,
  useCancelExecutionBatchMutation,
  useCancelExecutionJobMutation,
} = routerApi;
